
import { useState, useEffect } from 'react';

const useFullscreenStatus = (sendFullscreenExitEvent) => {
  const [isFullScreen, setIsFullScreen] = useState(false);
  const [timeElapsed, setTimeElapsed] = useState(0);

  // Function to enter fullscreen
  const enterFullScreen = () => {
    const element = document.documentElement;

    if (element.requestFullscreen) {
      element.requestFullscreen();
    } else if (element.mozRequestFullScreen) { // Firefox
      element.mozRequestFullScreen();
    } else if (element.webkitRequestFullscreen) { // Chrome, Safari, Opera
      element.webkitRequestFullscreen();
    } else if (element.msRequestFullscreen) { // IE/Edge
      element.msRequestFullscreen();
    }
  };

  // Function to check if the app is in fullscreen
  const checkFullScreenStatus = () => {
    if (
      document.fullscreenElement || // Standard
      document.mozFullScreenElement || // Firefox
      document.webkitFullscreenElement || // Chrome, Safari, Opera
      document.msFullscreenElement // IE/Edge
    ) {
      setIsFullScreen(true);
    } else {
      setIsFullScreen(false);
      sendFullscreenExitEvent();
    }
  };

  // Effect hook to listen for fullscreen changes
  useEffect(() => {
    // Listen for fullscreen change events
    document.addEventListener('fullscreenchange', checkFullScreenStatus);
    document.addEventListener('mozfullscreenchange', checkFullScreenStatus); // Firefox
    document.addEventListener('webkitfullscreenchange', checkFullScreenStatus); // Chrome, Safari
    document.addEventListener('msfullscreenchange', checkFullScreenStatus); // IE/Edge

    // Initial check on mount
    checkFullScreenStatus();

    // Cleanup listeners on component unmount
    return () => {
      document.removeEventListener('fullscreenchange', checkFullScreenStatus);
      document.removeEventListener('mozfullscreenchange', checkFullScreenStatus);
      document.removeEventListener('webkitfullscreenchange', checkFullScreenStatus);
      document.removeEventListener('msfullscreenchange', checkFullScreenStatus);
    };
  }, [sendFullscreenExitEvent]);

  // Timer for tracking how long the user has been out of fullscreen
  useEffect(() => {
    let intervalId;

    if (!isFullScreen) {
      intervalId = setInterval(() => {
        setTimeElapsed(prevTime => prevTime + 1);
      }, 1000); // Update every second
    } else {
      // Reset the timer when fullscreen is entered
      setTimeElapsed(0);
    }

    // Clean up the timer when fullscreen is active or on unmount
    return () => {
      if (intervalId) {
        clearInterval(intervalId);
      }
    };
  }, [isFullScreen]);

  return { isFullScreen, timeElapsed, enterFullScreen };
};

export default useFullscreenStatus;